import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { submissionAPI, emailAPI } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { Button, Badge, Input, Select, Modal, ConfirmModal } from './UI';
import ResumeButton from './ResumeButton';
import EmailTagInput from './EmailTagInput';
import InterviewerBusyWarning, { busyTargetOf } from './InterviewerBusyWarning';
import TimePicker12 from './TimePicker12';
import { fmtTimeRange, timeToMinutes, addMinutesToTime } from '../utils/format';
import RichTextEditor from './RichTextEditor';
import SafeHtml from './SafeHtml';

// ─────────────────────────────────────────────────────────────────────────────
// Shared "Update Status" modal — the single source of truth for changing a
// submission's status and editing its interview rounds. Used by both the
// Submissions page and the Interviews page so recruiters can update an interview
// from wherever they are.
//
// Props:
//   submission — the submission to edit (null when closed)
//   isOpen     — whether the modal is open
//   onClose    — called to close (parent clears its selection)
//   onSaved    — called after a successful save (parent refreshes its list)
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ['Applied', 'Screening', 'Shortlisted', 'Interview', 'Hold', 'Dropped', 'Rejected', 'Hired'];
const STATUS_VARIANT = { Applied: 'default', Screening: 'purple', Shortlisted: 'warning', Interview: 'info', Hold: 'amber', Dropped: 'indigo', Rejected: 'danger', Hired: 'success' };
const MODE_OPTIONS   = ['Online', 'In-Person', 'Phone'];
const BLANK_ROUND    = { date: '', time: '', endTime: '', mode: '', notes: '', title: '', resultDesc: '' };

// ── Interview slot helpers ────────────────────────────────────────────────────
// Default length pre-filled into End Time when a start time is picked.
const DEFAULT_SLOT_MINS = 30;

/** Apply a new start time to a round, keeping the end time coherent: fill it
 *  when blank, push it forward when the new start lands on/after it. An end the
 *  recruiter deliberately set longer/shorter is preserved. Clearing the start
 *  clears the end. */
const withStartTime = (round, time) => {
  const end = round?.endTime || '';
  const sm = timeToMinutes(time);
  const em = timeToMinutes(end);
  const keepEnd = sm != null && em != null && em > sm;
  return { ...round, time, endTime: keepEnd ? end : (addMinutesToTime(time, DEFAULT_SLOT_MINS) || '') };
};

/** Validation message for a round's slot, or null when it's fine. End time is
 *  mandatory once a start is picked — it drives the calendar invite and Teams
 *  meeting length. A date-only round stays valid (it becomes an all-day event). */
const slotError = (round, label) => {
  if (!round?.time) return null;
  if (!round.endTime) return `${label}: pick an end time for the interview.`;
  const sm = timeToMinutes(round.time);
  const em = timeToMinutes(round.endTime);
  if (sm == null || em == null || em <= sm) return `${label}: end time must be after the start time.`;
  return null;
};

/** True when a failed interview/cancel email request may still have been
 *  delivered: no response at all (browser timeout / dropped connection), a 504,
 *  or any error that is not the app's own JSON (a 502/503 page from
 *  openresty/Traefik). The proxy cuts requests at 60 s while the backend keeps
 *  sending, so "please try again" there would deliver a duplicate invite. The
 *  app's own JSON errors (including its 502 "could not be delivered") are real
 *  failures. */
const emailOutcomeUnknown = (err) => {
  const res = err?.response;
  if (!res || res.status === 504) return true;
  return !(res.data && typeof res.data === 'object' && typeof res.data.success === 'boolean');
};

// ── Interviewer prefill helpers ───────────────────────────────────────────────
// A round carries `interviewerEmails` (stamped server-side when its invite was
// sent). Both helpers return the comma-joined form EmailTagInput expects.

/** The interviewers actually invited to THIS round. */
const roundInterviewers = (round) =>
  (Array.isArray(round?.interviewerEmails) ? round.interviewerEmails : []).join(',');

/** Newest round carrying an interviewer list — the fallback seed when the round
 *  in hand has none (a brand-new round inherits the last panel used). */
const lastInterviewers = (rounds = []) =>
  roundInterviewers([...rounds].reverse().find(r => Array.isArray(r.interviewerEmails) && r.interviewerEmails.length));

const blankState = () => ({
  status: '', rejectionReason: '', holdReason: '', droppedReason: '',
  interviewRounds: [], addingRound: false, newRound: { ...BLANK_ROUND },
  reschedulingIdx: null,
  // Snapshot of each pre-existing round's original slot (date/time/mode/title),
  // keyed by a stable _origIdx, taken when the modal opens. Used at save time to
  // tell whether the emailed round had a DIFFERENT slot before (a reschedule) and
  // to cancel a deleted round on the candidate's calendar.
  origSnapshot: [],
  // Scheduled rounds the recruiter deleted this session — emailed as cancellations
  // (with an iCalendar CANCEL) on Save.
  cancelledRounds: [],
  sendEmail: false, toEmail: '', toName: '', attachResume: false,
  // Set by the per-round "Send invite" button when a round was scheduled but
  // never emailed. Keyed by the round's stable _origIdx (not its array index)
  // so deleting another round cannot silently retarget the invite.
  sendInviteOrigIdx: null,
  // Comma-separated interviewer email(s) — the invitation/reschedule/cancel
  // emails also go to them. Mandatory when the email section is active.
  interviewerEmails: '',
  // Optional rich-text note added to THIS invitation only — not stored on the
  // round, so it never silently reappears on a later reschedule.
  customMessage: '',
});

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
// Custom status picker — renders each option as a colored Badge, in a portal so
// the panel escapes the modal's overflow clipping.
function StatusPicker({ value, onChange, options, variants, label }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const panelRef   = useRef(null);

  const updateCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const maxLeft = typeof window !== 'undefined'
      ? Math.max(8, window.innerWidth - r.width - 8)
      : r.left;
    setCoords({ top: r.bottom + 4, left: Math.min(r.left, maxLeft), width: r.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateCoords();
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [open, updateCoords]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target))   return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div style={{ position: 'relative' }}>
      {label && (
        <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>{label}</label>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input-premium w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none"
        style={{
          background: 'var(--input-bg)', color: 'var(--text-primary)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          cursor: 'pointer', border: '1px solid var(--border-subtle)',
        }}
      >
        <span>{value ? <Badge variant={variants[value] || 'default'}>{value}</Badge> : <span style={{ color: 'var(--text-muted)' }}>Select a status…</span>}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: coords.top, left: coords.left, width: coords.width,
            zIndex: 1000000,
            background: 'var(--surface-1, #ffffff)',
            border: '1px solid var(--border-muted)',
            borderRadius: 10,
            boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            maxHeight: 320,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', background: opt === value ? 'var(--hover-bg)' : 'transparent',
                border: 'none', cursor: 'pointer', textAlign: 'left',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
              onMouseLeave={e => e.currentTarget.style.background = opt === value ? 'var(--hover-bg)' : 'transparent'}
            >
              <Badge variant={variants[opt] || 'default'}>{opt}</Badge>
              {opt === value && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ marginLeft: 'auto', color: '#10b981' }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default function StatusUpdateModal({ submission, isOpen, onClose, onSaved }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  // Requirement-level rule: force the interview invitation email and lock the
  // candidate's name/email.
  const lockEmail = !!submission?.requirement?.forceInterviewEmail;

  const [statusModal, setStatusModal] = useState(blankState());
  const [saving, setSaving] = useState(false);
  // Other interviews the chosen interviewer(s) already have in this slot, and
  // whether the recruiter chose to send anyway (asked once per save attempt).
  const [busyConflicts, setBusyConflicts] = useState([]);
  const [busyConfirm, setBusyConfirm] = useState(false);
  const busyAckRef = useRef(false);
  const busyTarget = busyTargetOf(statusModal);
  // Pending "cancel this interview round" confirmation — { origIdx, snap, when }
  // while the in-app ConfirmModal is open, null otherwise.
  const [cancelConfirm, setCancelConfirm] = useState(null);
  // Pending confirmation for the "send invitation email" toggle — { next } while
  // the dialog is open, null otherwise.
  const [emailConfirm, setEmailConfirm] = useState(null);
  // Interviewer type-ahead source. Fetched once per modal open; a failure just
  // means no suggestions — typing an address must never depend on this.
  const [interviewerSuggestions, setInterviewerSuggestions] = useState([]);
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    submissionAPI.interviewerSuggestions()
      .then(({ data }) => { if (!cancelled) setInterviewerSuggestions((data.emails || []).map(e => e.email)); })
      .catch(() => { if (!cancelled) setInterviewerSuggestions([]); });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Toggling the invitation email always confirms when switching it ON (it puts
  // real mail in the candidate's and interviewers' inboxes), and when switching
  // it OFF against a requirement that mandates it. Turning off an optional email
  // needs no ceremony.
  const requestSendEmailToggle = (next) => {
    if (!next && !lockEmail) return setStatusModal(m => ({ ...m, sendEmail: false }));
    setEmailConfirm({ next });
  };

  // Remove a round from the list (renumbering survivors); optionally queue it for
  // a cancellation email on Save. Re-finds the row by its stable _origIdx so it is
  // safe even if indices shifted while the confirm dialog was open.
  const removeRoundByOrigIdx = (origIdx, queueSnap) => {
    setStatusModal(m => {
      const i = m.interviewRounds.findIndex(r => r._origIdx === origIdx);
      if (i < 0) return m;
      const updated = m.interviewRounds.filter((_, k) => k !== i).map((rr, k) => ({ ...rr, round: k + 1 }));
      return {
        ...m,
        interviewRounds: updated,
        cancelledRounds: queueSnap ? [...(m.cancelledRounds || []), queueSnap] : (m.cancelledRounds || []),
        // Close the inline reschedule form if it was on the deleted round; shift it
        // down one slot if it was anchored below the deletion (indices moved up).
        reschedulingIdx: m.reschedulingIdx === i ? null
          : (m.reschedulingIdx > i ? m.reschedulingIdx - 1 : m.reschedulingIdx),
      };
    });
  };

  // (Re)initialize from the submission whenever the modal opens for a new one.
  // useLayoutEffect (not useEffect) so the reset runs synchronously BEFORE paint
  // — otherwise reopening for a different candidate flashes the previous one's
  // form values for one frame.
  useLayoutEffect(() => {
    if (!isOpen || !submission) return;
    // Migrate the legacy single-interview shape (flat interviewDate/interviewTime,
    // no interviewRounds) into a rounds array so the modal always works on rounds.
    const baseRounds = Array.isArray(submission.interviewRounds) && submission.interviewRounds.length > 0
      ? submission.interviewRounds
      : (submission.interviewDate ? [{
          round: 1, date: submission.interviewDate, time: submission.interviewTime || '',
          endTime: submission.interviewEndTime || '',
          mode: submission.interviewMode || '', notes: submission.interviewNotes || '', result: 'Pending',
        }] : []);
    // Tag each existing round with a stable original index (survives in-place edits
    // and delete-renumbering because every handler spreads {...rr}); newly added
    // rounds get no tag. origSnapshot records the pre-edit slot per tag so a
    // reschedule is detected reliably even for legacy records and after deletes.
    const rounds = baseRounds.map((r, i) => ({ ...r, _origIdx: i }));
    const origSnapshot = rounds.map(r => ({
      // The round's stable server id (absent on legacy rounds) — sent as roundId
      // when this round is cancelled, so the CANCEL hits the right calendar event
      // even after renumbering.
      id: r.id,
      // The UID scheme its invites went out under (e.g. legacy 'r<N>'). The PUT
      // has already removed the round when cancel runs, so only this lets the
      // CANCEL reach the event already on the calendars.
      calendarUidKey: r.calendarUidKey,
      origIdx: r._origIdx, round: r.round, date: r.date || '',
      time: r.time || '', endTime: r.endTime || '', mode: r.mode || '', title: r.title || '',
      // Signals the candidate was actually invited to this round — set only when
      // an invite email was sent (inviteSentAt) or a Teams meeting was minted
      // (meetingLink). Gates cancellation so we never "cancel" an uninvited round.
      inviteSentAt: r.inviteSentAt || '', meetingLink: r.meetingLink || '',
      // The interviewers who were actually invited to THIS round — cancellations
      // must notify them (not whatever the chip field currently holds).
      interviewerEmails: Array.isArray(r.interviewerEmails) ? r.interviewerEmails : [],
    }));
    setStatusModal({
      ...blankState(),
      status: submission.status,
      rejectionReason: submission.rejectionReason || '',
      holdReason:      submission.holdReason      || '',
      droppedReason:   submission.droppedReason   || '',
      interviewRounds: rounds,
      origSnapshot,
      addingRound: rounds.length === 0,
      // The requirement can force the interview invitation email on (and lock it).
      sendEmail:     !!submission.requirement?.forceInterviewEmail,
      // The resume is attached by default for every interview email; the
      // requirement additionally LOCKS it on so it can't be unticked.
      attachResume:  true,
      toEmail: submission.candidate?.email || '',
      toName:  submission.candidate?.fullName || '',
      // Prefill interviewer emails saved on a previous send for this submission.
      interviewerEmails: lastInterviewers(rounds),
    });
  }, [isOpen, submission]);

  const handleStatusSave = async () => {
    const busyAcknowledged = busyAckRef.current;
    busyAckRef.current = false;
    if (statusModal.status === 'Hold') {
      const plain = String(statusModal.holdReason || '').replace(/<[^>]*>/g, '').trim();
      if (!plain) return toast.error('Please provide a reason for putting this submission on hold.');
    }
    if (statusModal.status === 'Dropped') {
      const plain = String(statusModal.droppedReason || '').replace(/<[^>]*>/g, '').trim();
      if (!plain) return toast.error('Please provide a reason the candidate dropped out.');
    }

    // A brand-new round is being scheduled this save (vs. just editing an existing
    // round's result/notes). The invitation email fires ONLY for a newly scheduled
    // round or a reschedule — never for a plain Pass/Fail/notes change.
    const addedNewRound = statusModal.status === 'Interview' && statusModal.addingRound && !!statusModal.newRound.date;
    // A new-round form with content but NO date would be silently discarded on
    // save (the "Date *" label promises otherwise) — block instead of losing it.
    const newRoundDirty = ['title', 'time', 'mode', 'notes'].some(
      k => String(statusModal.newRound?.[k] || '').replace(/<[^>]*>/g, '').trim()
    );
    if (statusModal.status === 'Interview' && statusModal.addingRound && !statusModal.newRound.date && newRoundDirty) {
      return toast.error('Select a date for the new interview round (or clear the form).');
    }
    // End time is required on the round the recruiter is actually scheduling or
    // rescheduling. Deliberately NOT applied to every round — a plain Pass/Fail
    // save on a round that predates end times must not be blocked.
    if (statusModal.status === 'Interview') {
      const editing = [];
      if (statusModal.addingRound && statusModal.newRound.date) {
        editing.push([statusModal.newRound, `Round ${statusModal.interviewRounds.length + 1}`]);
      }
      const rIdx = statusModal.reschedulingIdx;
      if (rIdx != null && statusModal.interviewRounds[rIdx]) {
        const rr = statusModal.interviewRounds[rIdx];
        editing.push([rr, `Round ${rr.round || rIdx + 1}`]);
      }
      for (const [round, label] of editing) {
        const err = slotError(round, label);
        if (err) return toast.error(err);
      }
    }
    let finalRounds = statusModal.interviewRounds;
    if (addedNewRound) {
      finalRounds = [
        ...statusModal.interviewRounds,
        { ...statusModal.newRound, round: statusModal.interviewRounds.length + 1, result: 'Pending' },
      ];
    }
    // Which round this save will email. Normally the newest (a fresh round or a
    // reschedule); when the recruiter used a round's "Send invite" button it is
    // THAT round instead — resolved by its stable _origIdx so deleting another
    // round in the same session cannot silently retarget the invite. A target
    // that no longer exists (deleted before saving) simply cancels the send.
    const wantsOrigIdx = statusModal.sendInviteOrigIdx;
    const explicitIdx  = wantsOrigIdx == null ? -1 : finalRounds.findIndex(r => r._origIdx === wantsOrigIdx);
    const explicitSend = wantsOrigIdx != null && explicitIdx !== -1;
    const latestRound  = explicitSend ? finalRounds[explicitIdx] : finalRounds[finalRounds.length - 1];
    // Reschedule = the round we're emailing already existed and its date/time
    // changed → the email leads with a "rescheduled from → to" note. Compare the
    // emailed round against its own pre-edit slot via the stable _origIdx snapshot
    // (robust for legacy records and after other rounds are deleted/renumbered). A
    // newly added round has no _origIdx → no snapshot match → correctly not a
    // reschedule.
    const prevSlot = (statusModal.origSnapshot || []).find(s => s.origIdx === latestRound?._origIdx);
    // An end-time-only change still moves the calendar event's end, so it counts
    // as a reschedule and re-sends the invite.
    const isReschedule = !!(prevSlot && latestRound?.date
      && (prevSlot.date !== latestRound.date
          || (prevSlot.time    || '') !== (latestRound.time    || '')
          || (prevSlot.endTime || '') !== (latestRound.endTime || '')));
    const toCancel = statusModal.cancelledRounds || [];
    const interviewerList = String(statusModal.interviewerEmails || '').split(',').map(s => s.trim()).filter(Boolean);
    // Interviewer email(s) are mandatory whenever this save will send an interview
    // INVITE or RESCHEDULE — they receive the same email as the candidate.
    // (Cancellations are exempt: they notify the interviewers stored on the deleted
    // round itself, so a delete-only save never demands the chip field.)
    const willSendInterviewEmail = statusModal.status === 'Interview' && statusModal.sendEmail && statusModal.toEmail
      // explicitSend is the "I forgot to send it" path: the round is unchanged so
      // neither addedNewRound nor isReschedule holds, and without it the tick box
      // silently did nothing for an already-scheduled round.
      && latestRound?.date && (addedNewRound || isReschedule || explicitSend);
    if (willSendInterviewEmail && interviewerList.length === 0) {
      return toast.error('Add at least one interviewer email — they receive the interview email too.');
    }
    // An interviewer already booked for another interview in this slot: ask
    // before double-booking them (the warning is already shown in the form).
    if (willSendInterviewEmail && busyConflicts.length && !busyAcknowledged) {
      setBusyConfirm(true);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        status:          statusModal.status,
        rejectionReason: statusModal.status === 'Rejected' ? statusModal.rejectionReason : null,
        holdReason:      statusModal.status === 'Hold'     ? statusModal.holdReason     : null,
        droppedReason:   statusModal.status === 'Dropped'  ? statusModal.droppedReason  : null,
      };
      // Strip the internal _origIdx tag so it never gets persisted to the DB.
      // Each existing round's `id` is kept (every handler spreads {...rr}): the
      // server matches rounds by it, so deleting/renumbering never moves one
      // round's RSVPs, recordings or history onto another. New rounds go
      // without an id and the server assigns one.
      // Rounds are persisted for Interview saves AND whenever a deletion was
      // queued — otherwise switching status away from Interview in the same save
      // would silently discard the deletion (and its cancellation email).
      if (statusModal.status === 'Interview' || toCancel.length) {
        payload.interviewRounds = finalRounds.map(({ _origIdx, ...r }) => r);
      }

      const updRes = await submissionAPI.update(submission.id, payload);

      // Email cancellations for any scheduled rounds the recruiter deleted — each
      // removes the event from the candidate's calendar via an iCalendar CANCEL.
      // Not gated on status: a deleted invited round must be cancelled even if the
      // recruiter is simultaneously moving the submission to Rejected/Hold/etc.
      if (statusModal.toEmail && toCancel.length) {
        let cancelOk = 0;
        for (const cr of toCancel) {
          try {
            await emailAPI.cancelInterview({
              candidateId:   submission.candidateId,
              requirementId: submission.requirementId,
              submissionId:  submission.id,
              roundId:       cr.id || undefined,
              calendarUidKey: cr.calendarUidKey || undefined,
              roundNumber:   cr.round || 1,
              roundTitle:    cr.title || '',
              interviewDate:    cr.date,
              interviewTime:    cr.time,
              interviewEndTime: cr.endTime || '',
              interviewMode:    cr.mode || '',
              toEmail:       statusModal.toEmail,
              toName:        statusModal.toName,
              // Cancel notifies the interviewers who were INVITED to this round
              // (stored on it at invite time); fall back to the current chip list
              // for rounds invited before the list was being stored.
              interviewerEmails: (cr.interviewerEmails && cr.interviewerEmails.length) ? cr.interviewerEmails : interviewerList,
            });
            cancelOk += 1;
          } catch (err) {
            if (!emailOutcomeUnknown(err)) {
              toast.error(`Round deleted, but cancellation email failed: ${err.response.data?.message || 'please try again.'}`);
            } else {
              toast('Round deleted. The cancellation email is still sending — check Email Logs before resending.', { icon: '⏳', duration: 8000 });
            }
          }
        }
        if (cancelOk > 0) toast.success(`Interview ${cancelOk > 1 ? 'rounds' : 'round'} cancelled — candidate notified.`);
      }

      if (willSendInterviewEmail) {
        // Stable id of the round being emailed. A round added in this save has
        // none yet — read the id the server just assigned from the saved copy
        // (same array order as sent). A legacy round that predates ids is sent
        // without one so the server keeps its round-number calendar UID.
        const savedRounds = updRes?.data?.submission?.interviewRounds;
        const sendIdx = explicitSend ? explicitIdx : finalRounds.length - 1;
        const isNewRound = latestRound._origIdx == null;
        const roundId = latestRound.id
          || (isNewRound && Array.isArray(savedRounds) && savedRounds.length === finalRounds.length
            ? savedRounds[sendIdx]?.id : undefined);
        try {
          const sendRes = await emailAPI.sendInterview({
            candidateId:   submission.candidateId,
            requirementId: submission.requirementId,
            submissionId:  submission.id,   // lets the backend mint/reuse a Teams join link on this round
            isReschedule,
            previousDate:    isReschedule ? prevSlot.date : '',
            previousTime:    isReschedule ? (prevSlot.time    || '') : '',
            previousEndTime: isReschedule ? (prevSlot.endTime || '') : '',
            toEmail:       statusModal.toEmail,
            toName:        statusModal.toName,
            interviewerEmails: interviewerList,
            customMessage: statusModal.customMessage,
            attachResume:  statusModal.attachResume,
            interviewDate:    latestRound.date,
            interviewTime:    latestRound.time,
            interviewEndTime: latestRound.endTime || '',
            interviewMode:    latestRound.mode,
            // Names the calendar event + keeps its UID stable per round so a
            // reschedule UPDATES the invite on the candidate's calendar.
            roundTitle:    latestRound.title || '',
            roundId:       roundId || undefined,
            roundNumber:   latestRound.round || finalRounds.length,
          });
          // The endpoint reports per-recipient delivery. Surface it: a silent
          // "sent!" while an interviewer's copy bounced is exactly how a missing
          // invite goes unnoticed until the interview doesn't happen.
          const results  = Array.isArray(sendRes?.data?.results) ? sendRes.data.results : [];
          const failedTo = results.filter(r => r.status === 'failed').map(r => r.to);
          const ivrOk    = results.filter(r => r.type === 'interviewer' && r.status === 'sent');
          const recOk    = results.find(r => r.type === 'recruiter' && r.status === 'sent');
          if (failedTo.length) {
            toast.error(`Status updated, but the interview email failed for: ${failedTo.join(', ')}`, { duration: 9000 });
          } else {
            toast.success(
              `Status updated & interview email sent — candidate${ivrOk.length ? ` + ${ivrOk.length} interviewer${ivrOk.length > 1 ? 's' : ''}` : ''}${recOk ? ` + recruiter (${recOk.name || recOk.to})` : ''}!`
            );
          }
        } catch (err) {
          // No response / gateway timeout = the send may well have completed
          // server-side, so never tell the recruiter to simply retry — that
          // would deliver a duplicate invite.
          if (!emailOutcomeUnknown(err)) {
            toast.error(`Status updated, but interview email failed: ${err.response.data?.message || 'please try again.'}`);
          } else {
            toast('Status updated. The interview email is still sending — check Email Logs before resending.', { icon: '⏳', duration: 8000 });
          }
        }
      } else {
        toast.success('Status updated!');
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status.');
    } finally { setSaving(false); }
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose}
      title={`Update Status — ${submission?.candidate?.fullName || ''}`} size="lg">
      <div className="space-y-4">
        <StatusPicker
          label="Status"
          value={statusModal.status}
          onChange={s => setStatusModal(m => ({ ...m, status: s }))}
          options={STATUS_OPTIONS}
          variants={STATUS_VARIANT}
        />

        {statusModal.status === 'Rejected' && (
          <RichTextEditor label="Rejection Reason" value={statusModal.rejectionReason} rows={3}
            onChange={(html) => setStatusModal(m => ({ ...m, rejectionReason: html }))}
            placeholder="Why was the candidate rejected?" />
        )}

        {statusModal.status === 'Hold' && (
          <RichTextEditor label="Hold Reason *" value={statusModal.holdReason} rows={3}
            onChange={(html) => setStatusModal(m => ({ ...m, holdReason: html }))}
            placeholder="Why is this submission on hold? (e.g. waiting on client decision, candidate unavailable…)" />
        )}

        {statusModal.status === 'Dropped' && (
          <RichTextEditor label="Drop Reason *" value={statusModal.droppedReason} rows={3}
            onChange={(html) => setStatusModal(m => ({ ...m, droppedReason: html }))}
            placeholder="Why did the candidate drop out? (e.g. accepted another offer, not interested anymore…)" />
        )}

        {statusModal.status === 'Interview' && (() => {
          const rounds       = statusModal.interviewRounds;
          const allDone      = rounds.length > 0 && rounds.every(r => r.result && r.result !== 'Pending');
          const canAddRound  = !statusModal.addingRound && (rounds.length === 0 || allDone);
          // The email card appears only when THIS save would send an invite:
          // a new round being scheduled, or the latest round's slot changed
          // (a reschedule). Mirrors the send condition — plain result/notes
          // edits never show (or send) the interview email.
          const latestR      = rounds[rounds.length - 1];
          const latestSnap   = (statusModal.origSnapshot || []).find(s => s.origIdx === latestR?._origIdx);
          // A round explicitly picked via "Send invite" also shows the card — that
          // is the whole point of the forgot-to-send path, where nothing about the
          // round has changed. Checked to still exist, so deleting the target in the
          // same session hides the card again, matching what save will do.
          const invitingRound = statusModal.sendInviteOrigIdx != null
            && rounds.some(r => r._origIdx === statusModal.sendInviteOrigIdx);
          const willEmail    = statusModal.addingRound || invitingRound || !!(latestSnap && latestR?.date
            && (latestSnap.date !== latestR.date
                || (latestSnap.time    || '') !== (latestR.time    || '')
                || (latestSnap.endTime || '') !== (latestR.endTime || '')));
          const resultColor  = (r) => r === 'Pass' ? (isDark ? '#6ee7b7' : '#047857') : r === 'Fail' ? (isDark ? '#fca5a5' : '#b91c1c') : (isDark ? '#fcd34d' : '#b45309');
          const resultBg     = (r) => r === 'Pass' ? (isDark ? 'rgba(16,185,129,0.15)' : '#ecfdf5') : r === 'Fail' ? (isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2') : (isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb');
          const resultBorder = (r) => r === 'Pass' ? (isDark ? 'rgba(16,185,129,0.3)' : '#a7f3d0') : r === 'Fail' ? (isDark ? 'rgba(239,68,68,0.3)' : '#fecaca') : (isDark ? 'rgba(245,158,11,0.3)' : '#fde68a');
          return (
            <div style={{ borderRadius: 10, border: `1px solid ${isDark ? 'rgba(99,102,241,0.25)' : '#ddd6fe'}`, padding: 16, background: isDark ? 'rgba(99,102,241,0.07)' : '#f5f3ff' }} className="space-y-4">
              <p style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#a5b4fc' : '#6366f1' }}>🎯 Interview Rounds</p>

              {rounds.length > 0 && (
                <div className="space-y-3">
                  {rounds.map((r, idx) => (
                    <div key={idx} style={{ borderRadius: 8, border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e0d9ff'}`, padding: '10px 12px', background: isDark ? 'rgba(0,0,0,0.2)' : '#ffffff' }} className="space-y-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#a5b4fc' : '#6366f1' }}>Round {r.round || idx + 1}</span>
                          {r.title && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', marginLeft: 6 }}>· {r.title}</span>}
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 6 }}>
                            {formatDate(r.date)}{r.time ? ` · ${fmtTimeRange(r.time, r.endTime)}` : ''}{r.mode ? ` · ${r.mode}` : ''}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                          {['Pending', 'Pass', 'Fail'].map(res => (
                            <button key={res} type="button"
                              onClick={() => setStatusModal(m => ({
                                ...m,
                                interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, result: res } : rr),
                              }))}
                              style={{
                                fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 600, cursor: 'pointer', border: '1px solid',
                                background:  r.result === res ? resultBg(res)     : 'transparent',
                                color:       r.result === res ? resultColor(res)  : 'var(--text-muted)',
                                borderColor: r.result === res ? resultBorder(res) : 'var(--border-subtle)',
                              }}>
                              {res}
                            </button>
                          ))}
                          {/* "Forgot to send it" — a round can be scheduled without
                              ever emailing anyone, and until now there was no way to
                              send the invite afterwards: ticking the email box only
                              took effect for a NEW or RESCHEDULED round. Offered only
                              while the round has a date and no invite actually went out. */}
                          {(!r.result || r.result === 'Pending') && r.date && (
                            <button key="send-invite" type="button"
                              title={r.inviteSentAt
                                ? `Invitation already sent ${new Date(r.inviteSentAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })} — send it again`
                                : 'Send the interview invitation for this round'}
                              onClick={() => setStatusModal(m => {
                                const cur = m.interviewRounds[idx];
                                if (m.sendInviteOrigIdx === cur?._origIdx) return { ...m, sendInviteOrigIdx: null };
                                return {
                                  ...m,
                                  sendInviteOrigIdx: cur?._origIdx ?? null,
                                  // Reveal the email section and seed the panel exactly
                                  // as Reschedule does, so nothing is retyped.
                                  sendEmail: true,
                                  interviewerEmails: roundInterviewers(cur)
                                    || lastInterviewers(m.interviewRounds)
                                    || m.interviewerEmails,
                                };
                              })}
                              style={{
                                fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 600, cursor: 'pointer', border: '1px solid',
                                background:  statusModal.sendInviteOrigIdx === r._origIdx ? (isDark ? 'rgba(16,185,129,0.18)' : '#d1fae5') : 'transparent',
                                color:       statusModal.sendInviteOrigIdx === r._origIdx ? (isDark ? '#6ee7b7' : '#047857') : 'var(--text-muted)',
                                borderColor: statusModal.sendInviteOrigIdx === r._origIdx ? (isDark ? 'rgba(16,185,129,0.4)' : '#a7f3d0') : 'var(--border-subtle)',
                              }}>
                              {r.inviteSentAt ? '✉ Resend invite' : '✉ Send invite'}
                            </button>
                          )}
                          {(!r.result || r.result === 'Pending') && (
                            <button key="reschedule" type="button" title="Reschedule this round"
                              onClick={() => setStatusModal(m => {
                                const opening = m.reschedulingIdx !== idx;
                                if (!opening) return { ...m, reschedulingIdx: null };
                                const cur = m.interviewRounds[idx];
                                // Rounds scheduled before end times existed have a start but no
                                // end. Backfill the default slot so the now-required field opens
                                // pre-filled — and mirror it into origSnapshot so this backfill
                                // alone isn't mistaken for a reschedule (which would email the
                                // candidate even though nothing actually moved).
                                const backfill = !!(cur?.time && !cur?.endTime);
                                const endTime  = backfill ? (addMinutesToTime(cur.time, DEFAULT_SLOT_MINS) || '') : cur?.endTime;
                                return {
                                  ...m,
                                  reschedulingIdx: idx,
                                  interviewRounds: backfill
                                    ? m.interviewRounds.map((rr, i) => i === idx ? { ...rr, endTime } : rr)
                                    : m.interviewRounds,
                                  origSnapshot: backfill
                                    ? (m.origSnapshot || []).map(s => s.origIdx === cur._origIdx ? { ...s, endTime } : s)
                                    : m.origSnapshot,
                                  // Re-seed the chips with the interviewers actually invited to
                                  // THAT round, so the recruiter never retypes them. Falls back to
                                  // the last panel used, then to whatever is already typed.
                                  interviewerEmails: roundInterviewers(cur)
                                    || lastInterviewers(m.interviewRounds)
                                    || m.interviewerEmails,
                                };
                              })}
                              style={{
                                fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 600, cursor: 'pointer', border: '1px solid',
                                background:  statusModal.reschedulingIdx === idx ? (isDark ? 'rgba(99,102,241,0.18)' : '#e0e7ff') : 'transparent',
                                color:       statusModal.reschedulingIdx === idx ? (isDark ? '#a5b4fc' : '#4338ca') : 'var(--text-muted)',
                                borderColor: statusModal.reschedulingIdx === idx ? (isDark ? 'rgba(99,102,241,0.4)' : '#c7d2fe') : 'var(--border-subtle)',
                              }}>
                              🔁 Reschedule
                            </button>
                          )}
                          {(!r.result || r.result === 'Pending') && (
                          <button type="button" title="Delete this round"
                            onClick={() => {
                              // Deleting an upcoming round always asks for confirmation. If the
                              // candidate was actually INVITED (invite email sent / Teams meeting
                              // minted) the confirm also queues a cancellation email + calendar
                              // removal; otherwise it just removes the round.
                              const removed = statusModal.interviewRounds[idx];
                              const snap = (statusModal.origSnapshot || []).find(s => s.origIdx === removed?._origIdx);
                              const wasInvited = !!(snap && snap.date && (snap.inviteSentAt || snap.meetingLink));
                              setCancelConfirm({
                                origIdx: removed?._origIdx,
                                snap: wasInvited ? snap : null,   // queue a cancellation only for invited rounds
                                when: snap ? [snap.date, snap.time].filter(Boolean).join(' ') : '',
                                wasInvited,
                              });
                            }}
                            style={{
                              marginLeft: 6, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: 30, height: 30, borderRadius: 8, cursor: 'pointer', transition: 'all 0.12s',
                              color: isDark ? '#fca5a5' : '#dc2626',
                              background: isDark ? 'rgba(239,68,68,0.16)' : 'rgba(239,68,68,0.10)',
                              border: `1px solid ${isDark ? 'rgba(239,68,68,0.40)' : 'rgba(239,68,68,0.30)'}`,
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#ef4444'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = isDark ? 'rgba(239,68,68,0.16)' : 'rgba(239,68,68,0.10)'; e.currentTarget.style.color = isDark ? '#fca5a5' : '#dc2626'; e.currentTarget.style.borderColor = isDark ? 'rgba(239,68,68,0.40)' : 'rgba(239,68,68,0.30)'; }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              <line x1="10" y1="11" x2="10" y2="17" />
                              <line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                          </button>
                          )}
                        </div>
                      </div>
                      {statusModal.reschedulingIdx === idx && (
                        <div style={{ borderTop: `1px dashed ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`, paddingTop: 10, marginTop: 6 }} className="space-y-2">
                          <p style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#a5b4fc' : '#6366f1' }}>
                            🔁 Reschedule Round {r.round || idx + 1}
                          </p>
                          <Input label="Interview Title (optional)"
                            placeholder="e.g. Technical Round, HR Interview…"
                            value={r.title || ''}
                            onChange={e => setStatusModal(m => ({
                              ...m,
                              interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, title: e.target.value } : rr),
                            }))} />
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <Input label="Date *" type="date"
                              value={r.date || ''}
                              onChange={e => setStatusModal(m => ({
                                ...m,
                                interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, date: e.target.value } : rr),
                              }))} />
                            <TimePicker12 label="Start Time"
                              value={r.time || ''}
                              onChange={v => setStatusModal(m => ({
                                ...m,
                                interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? withStartTime(rr, v) : rr),
                              }))} />
                            <TimePicker12 label={r.time ? 'End Time *' : 'End Time'}
                              value={r.endTime || ''}
                              onChange={v => setStatusModal(m => ({
                                ...m,
                                interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, endTime: v } : rr),
                              }))} />
                            <Select label="Mode"
                              value={r.mode || ''}
                              onChange={e => setStatusModal(m => ({
                                ...m,
                                interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, mode: e.target.value } : rr),
                              }))}>
                              <option value="">— Select —</option>
                              <option>In-Person</option>
                              <option>Online</option>
                              <option>Phone</option>
                            </Select>
                          </div>
                          <RichTextEditor label="Notes (optional)" rows={3}
                            value={r.notes || ''}
                            onChange={(html) => setStatusModal(m => ({
                              ...m,
                              interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, notes: html } : rr),
                            }))} />
                          <div className="flex justify-end">
                            <Button size="xs" variant="primary"
                              onClick={() => setStatusModal(m => ({ ...m, reschedulingIdx: null }))}>
                              Done
                            </Button>
                          </div>
                        </div>
                      )}
                      {r.notes && statusModal.reschedulingIdx !== idx && (
                        <SafeHtml html={r.notes} style={{ fontSize: 11, color: 'var(--text-muted)' }} />
                      )}
                      {r.result && r.result !== 'Pending' && (
                        <div>
                          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>
                            Result Notes {r.result === 'Pass' ? '✅' : '❌'}
                          </label>
                          <RichTextEditor
                            rows={3}
                            placeholder={r.result === 'Pass' ? 'What went well? Any feedback…' : 'Reason for rejection or areas of concern…'}
                            value={r.resultDesc || ''}
                            onChange={(html) => setStatusModal(m => ({
                              ...m,
                              interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, resultDesc: html } : rr),
                            }))}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {canAddRound && (
                <button type="button"
                  onClick={() => setStatusModal(m => ({ ...m, addingRound: true, newRound: { ...BLANK_ROUND } }))}
                  style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#a5b4fc' : '#6366f1', background: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff', border: `1px dashed ${isDark ? 'rgba(99,102,241,0.4)' : '#a5b4fc'}`, borderRadius: 8, padding: '8px 14px', cursor: 'pointer', width: '100%' }}>
                  + Schedule Round {rounds.length + 1}
                </button>
              )}

              {statusModal.addingRound && (
                <div style={{ borderRadius: 8, border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`, padding: 12, background: isDark ? 'rgba(99,102,241,0.06)' : '#f5f3ff' }} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#a5b4fc' : '#6366f1' }}>
                      📅 Schedule Round {rounds.length + 1}
                    </p>
                    {rounds.length > 0 && (
                      <button type="button" onClick={() => setStatusModal(m => ({ ...m, addingRound: false }))}
                        style={{ fontSize: 18, lineHeight: 1, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
                    )}
                  </div>
                  <Input label="Interview Title (optional)"
                    placeholder="e.g. Technical Round, HR Interview, Manager Round…"
                    value={statusModal.newRound.title}
                    onChange={e => setStatusModal(m => ({ ...m, newRound: { ...m.newRound, title: e.target.value } }))} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <Input label="Date *" type="date"
                      value={statusModal.newRound.date}
                      onChange={e => setStatusModal(m => ({ ...m, newRound: { ...m.newRound, date: e.target.value } }))} />
                    <TimePicker12 label="Start Time"
                      value={statusModal.newRound.time}
                      onChange={v => setStatusModal(m => ({ ...m, newRound: withStartTime(m.newRound, v) }))} />
                    <TimePicker12 label={statusModal.newRound.time ? 'End Time *' : 'End Time'}
                      value={statusModal.newRound.endTime}
                      onChange={v => setStatusModal(m => ({ ...m, newRound: { ...m.newRound, endTime: v } }))} />
                    <Select label="Mode"
                      value={statusModal.newRound.mode}
                      onChange={e => setStatusModal(m => ({ ...m, newRound: { ...m.newRound, mode: e.target.value } }))}>
                      <option value="">— Select —</option>
                      {MODE_OPTIONS.map(mo => <option key={mo}>{mo}</option>)}
                    </Select>
                  </div>
                  <RichTextEditor label="Notes (optional)" rows={3}
                    value={statusModal.newRound.notes}
                    onChange={(html) => setStatusModal(m => ({ ...m, newRound: { ...m.newRound, notes: html } }))} />
                </div>
              )}

              {/* Interview invitation email is a requirement-level opt-in — the card
                  appears only when the toggle is ON AND this save would actually
                  send an invite (new round or reschedule). */}
              {willEmail && (
                <div style={{ borderTop: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : '#ddd6fe'}`, paddingTop: 10 }}>
                  <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={statusModal.sendEmail}
                      onChange={e => requestSendEmailToggle(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                      className="w-4 h-4 rounded accent-indigo-500" />
                    <span style={{ fontSize: 13, color: isDark ? '#a5b4fc' : '#4f46e5', fontWeight: 500 }}>
                      📧 Send interview invitation email to candidate <span style={{ fontWeight: 400, opacity: 0.75 }}>(includes 📅 calendar invite)</span>
                    </span>
                    {lockEmail && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#fbbf24' : '#b45309', background: isDark ? 'rgba(251,191,36,0.12)' : '#fef3c7', borderRadius: 6, padding: '1px 7px' }}>🔒 Required by requirement</span>
                    )}
                  </label>
                  {statusModal.sendEmail && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      <Input label="Candidate Email" value={statusModal.toEmail} disabled={lockEmail}
                        title={lockEmail ? 'Locked by requirement setting' : undefined}
                        onChange={e => setStatusModal(m => ({ ...m, toEmail: e.target.value }))} />
                      <Input label="Candidate Name" value={statusModal.toName} disabled={lockEmail}
                        title={lockEmail ? 'Locked by requirement setting' : undefined}
                        onChange={e => setStatusModal(m => ({ ...m, toName: e.target.value }))} />
                      <div className="col-span-2">
                        <EmailTagInput label="Interviewer Email(s)" required isDark={isDark}
                          suggestions={interviewerSuggestions}
                          value={statusModal.interviewerEmails}
                          onChange={v => setStatusModal(m => ({ ...m, interviewerEmails: v }))}
                          placeholder="interviewer@company.com — press Enter to add more" />
                        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                          The interviewer(s) receive the same interview email and calendar invite as the candidate.
                        </p>
                        <InterviewerBusyWarning isDark={isDark}
                          emails={statusModal.interviewerEmails}
                          date={busyTarget?.date} time={busyTarget?.time} endTime={busyTarget?.endTime}
                          submissionId={submission?.id} roundId={busyTarget?.id} roundNumber={busyTarget?.round}
                          onChange={setBusyConflicts} />
                      </div>
                      {/* Optional per-send note. Goes to everyone who receives
                          this invitation, so it reads as part of the invite. */}
                      <div className="col-span-2">
                        <RichTextEditor label="Custom message (optional)" rows={4}
                          value={statusModal.customMessage}
                          onChange={(html) => setStatusModal(m => ({ ...m, customMessage: html }))}
                          placeholder="Anything to add for this interview — e.g. what to prepare, who they'll meet, documents to carry…" />
                        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                          Added to this email only — the candidate, interviewer(s) and co-owners all see it.
                          Templates can place it with <code>{'{{custom_message}}'}</code>; otherwise it appears
                          after the invitation details.
                        </p>
                      </div>
                      <label className="col-span-2 flex items-center gap-2 mt-1"
                        style={{
                          cursor: lockEmail ? 'not-allowed' : 'pointer',
                          background: statusModal.attachResume ? (isDark ? 'rgba(16,185,129,0.08)' : '#f0fdf4') : 'var(--bg-raised)',
                          border: `1px solid ${statusModal.attachResume ? (isDark ? 'rgba(16,185,129,0.3)' : '#a7f3d0') : 'var(--border-subtle)'}`,
                          borderRadius: 8, padding: '8px 12px',
                        }}>
                        <input type="checkbox" checked={statusModal.attachResume} disabled={lockEmail}
                          onChange={e => setStatusModal(m => ({ ...m, attachResume: e.target.checked }))}
                          className="w-4 h-4 accent-emerald-500" style={{ cursor: lockEmail ? 'not-allowed' : 'pointer' }} />
                        <span style={{ fontSize: 13, fontWeight: 500, color: statusModal.attachResume ? (isDark ? '#6ee7b7' : '#047857') : 'var(--text-secondary)' }}>
                          📎 Attach candidate's resume (PDF)
                        </span>
                        {/* This box is ticked by DEFAULT and the requirement can
                            lock it on, so without these two the invite could go
                            out with no attachment and nothing on screen said so —
                            the recruiter believes the interviewer got the CV. */}
                        {statusModal.attachResume && !submission?.candidate?.resumeName && (
                          <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: isDark ? '#f87171' : '#dc2626' }}>
                            ⚠ No resume on file — nothing will be attached
                          </span>
                        )}
                        {statusModal.attachResume && submission?.candidate?.resumeName && (
                          <span
                            onClick={e => e.preventDefault()}
                            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}
                          >
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>
                              {submission.candidate.resumeName}
                            </span>
                            <ResumeButton
                              variant="link"
                              candidateId={submission.candidate.id}
                              resumeName={submission.candidate.resumeName}
                              label="View"
                            />
                          </span>
                        )}
                        {lockEmail && (
                          <span style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#fbbf24' : '#b45309', background: isDark ? 'rgba(251,191,36,0.12)' : '#fef3c7', borderRadius: 6, padding: '1px 7px' }}>🔒</span>
                        )}
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleStatusSave} loading={saving}>Save</Button>
        </div>
      </div>
    </Modal>

    <ConfirmModal
      isOpen={busyConfirm}
      danger
      title="Interviewer already busy at this time"
      confirmLabel="Send anyway"
      message={`${[...new Set(busyConflicts.map(c => c.email))].join(', ')} already ${busyConflicts.length > 1 ? 'have' : 'has'} an interview in this slot. Sending will double-book them — pick another time or interviewer unless this is intended.`}
      onCancel={() => setBusyConfirm(false)}
      onConfirm={() => { setBusyConfirm(false); busyAckRef.current = true; handleStatusSave(); }}
    />

    <ConfirmModal
      isOpen={!!cancelConfirm}
      danger
      title={cancelConfirm?.wasInvited ? 'Cancel this interview round?' : 'Delete this interview round?'}
      confirmLabel={cancelConfirm?.wasInvited ? 'Yes, cancel it' : 'Delete round'}
      message={cancelConfirm?.wasInvited
        ? `The candidate will be emailed that their interview${cancelConfirm?.when ? ` scheduled for ${cancelConfirm.when}` : ''} is cancelled, and it will be removed from their calendar when you click Save.`
        : `This interview round${cancelConfirm?.when ? ` (${cancelConfirm.when})` : ''} will be removed when you click Save.`}
      onCancel={() => setCancelConfirm(null)}
      onConfirm={() => { if (cancelConfirm) removeRoundByOrigIdx(cancelConfirm.origIdx, cancelConfirm.snap); setCancelConfirm(null); }}
    />

    <ConfirmModal
      isOpen={!!emailConfirm}
      danger={emailConfirm?.next === false}
      title={emailConfirm?.next
        ? 'Send the interview invitation email?'
        : 'Skip the interview invitation email?'}
      confirmLabel={emailConfirm?.next ? 'Yes, send it' : 'Yes, skip it'}
      message={emailConfirm?.next
        ? 'When you click Save, the candidate and every interviewer you list will be emailed this interview invitation, each with a calendar invite.'
        : (lockEmail
            ? 'This requirement is set to always send the interview invitation. If you skip it, the candidate will NOT be told about this interview and will get no calendar invite.'
            : 'The candidate will not be emailed about this interview.')}
      onCancel={() => setEmailConfirm(null)}
      onConfirm={() => {
        setStatusModal(m => ({ ...m, sendEmail: !!emailConfirm?.next }));
        setEmailConfirm(null);
      }}
    />
    </>
  );
}
